import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    useMemo,
} from "react";
import {
    Box,
    ButtonBase,
    CircularProgress,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CameraAltRounded from "@mui/icons-material/CameraAltRounded";
import LocationOnRounded from "@mui/icons-material/LocationOnRounded";
import LocalPhoneRounded from "@mui/icons-material/LocalPhoneRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import Instagram from "@mui/icons-material/Instagram";
import X from "@mui/icons-material/X";
import Facebook from "@mui/icons-material/Facebook";
import LinkRounded from "@mui/icons-material/LinkRounded";
import TextField from "../../TextField";
import Account, { CustomLink } from "../../../api/Account";
import { useCookies } from "react-cookie";
import BottomButton from "../../BottomButton";
import { getPreferredLanguage } from "../../../utils/languageUtils";
import AIDescriptionField from "../../AIDescriptionField";
import { CustomError } from "../../../types";
import {
    getCloudinaryOriginalUrl,
    uploadToCloudinary,
} from "../../../utils/cloudinaryUtils";
import { useNavigate } from "react-router-dom";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import ImageCropperModal from "../../ImageCropperModal";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";
import { useFilePicker } from "use-file-picker";
import {
    FileAmountLimitValidator,
    FileTypeValidator,
    FileSizeValidator,
} from "use-file-picker/validators";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const ProfileEdit: React.FC = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [account, setAccount] = useState<Account | undefined>();
    const [image, setImage] = useState<string | null | undefined>();
    const [imagePreview, setImagePreview] = useState<string | undefined>();
    const [fullName, setFullName] = useState("");
    const [description, setDescription] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [instagram, setInstagram] = useState("");
    const [twitter, setTwitter] = useState("");
    const [facebook, setFacebook] = useState("");
    const [customLinks, setCustomLinks] = useState<CustomLink[]>([]);
    const [descriptionError, setDescriptionError] = useState<
        string | undefined
    >();
    const [isSaving, setIsSaving] = useState(false);
    const [customLinkErrors, setCustomLinkErrors] = useState<
        { label: boolean; url: boolean }[]
    >([]);
    const [customLinkTouched, setCustomLinkTouched] = useState<
        { label: boolean; url: boolean }[]
    >([]);

    // crop modal state
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);

    // scroll tracking
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    // derive original for recropping (strip transforms)
    const originalForCrop = useMemo(() => {
        return imagePreview ? getCloudinaryOriginalUrl(imagePreview) : null;
    }, [imagePreview]);

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    // File picker for avatar
    const { openFilePicker: openAvatarPicker } = useFilePicker({
        readAs: "DataURL",
        accept: "image/*",
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(["jpeg", "jpg", "png", "heic", "heif"]),
            new FileSizeValidator({ maxFileSize: 10 * 1024 * 1024 }),
        ],
        onFilesSelected: ({ errors }) => {
            if (errors?.length) console.error("File upload error:", errors);
        },
        onFilesSuccessfullySelected: ({ filesContent }) => {
            if (filesContent[0]) onProfileImageUpload(filesContent[0].content);
        },
    });

    const addressInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const instagramInputRef = useRef<HTMLInputElement>(null);
    const twitterInputRef = useRef<HTMLInputElement>(null);
    const facebookInputRef = useRef<HTMLInputElement>(null);
    const customLinkRefs = useRef<
        {
            labelRef: React.RefObject<HTMLInputElement>;
            urlRef: React.RefObject<HTMLInputElement>;
        }[]
    >([]);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        if (!uuid) navigate("/");

        Account.info(uuid).then((a) => {
            if (!a) return;
            setAccount(a);
            setImagePreview(
                a.image ||
                    sessionStorage.getItem(`${a.name}_image`) ||
                    undefined
            );

            setFullName(a.name ?? "");
            setDescription(a.about ?? "");
            setAddress(a.address ?? "");
            setPhone(a.phone ?? "");
            setEmail(a.contactEmail ?? "");
            setInstagram(a.instagram ?? "");
            setTwitter(a.x_twitter ?? "");
            setFacebook(a.facebook ?? "");
            setCustomLinks(a.custom_links ?? []);
        });
    }, [uuid, navigate]);

    useEffect(() => {
        if (imagePreview && account?.name) {
            sessionStorage.setItem(`${account.name}_image`, imagePreview);
        }
    }, [account, imagePreview]);

    // Keep customLinkRefs in sync
    while (customLinkRefs.current.length < customLinks.length) {
        customLinkRefs.current.push({
            labelRef: React.createRef(),
            urlRef: React.createRef(),
        });
    }
    while (customLinkRefs.current.length > customLinks.length) {
        customLinkRefs.current.pop();
    }

    const isValidUrl = (url: string): boolean =>
        url.trim() !== "" && /^https?:\/\/[^\s$.?#].[^\s]*$/i.test(url.trim());

    const updateCustomLink = (
        index: number,
        key: "label" | "url",
        value: string
    ) => {
        const updated = [...customLinks];
        updated[index][key] = value;
        setCustomLinks(updated);
        if (customLinkTouched[index]?.[key])
            validateCustomLink(index, key, value);
    };

    const removeCustomLink = (index: number) => {
        setCustomLinks(customLinks.filter((_, i) => i !== index));
        setCustomLinkErrors(customLinkErrors.filter((_, i) => i !== index));
        setCustomLinkTouched(customLinkTouched.filter((_, i) => i !== index));
    };

    const touchCustomLink = (index: number, key: "label" | "url") => {
        const touched = [...customLinkTouched];
        if (!touched[index]) touched[index] = { label: false, url: false };
        touched[index][key] = true;
        setCustomLinkTouched(touched);
        validateCustomLink(index, key, customLinks[index][key]);
    };

    const validateCustomLink = (
        index: number,
        key: "label" | "url",
        value: string
    ) => {
        const errs = [...customLinkErrors];
        if (!errs[index]) errs[index] = { label: false, url: false };
        if (key === "label") errs[index].label = value.trim() === "";
        else errs[index].url = !isValidUrl(value);
        setCustomLinkErrors(errs);
    };

    const hasErrors = useMemo(() => {
        return customLinks.some((_, i) => {
            const e = customLinkErrors[i];
            const t = customLinkTouched[i];
            if (!e || !t) return false;
            return (t.label && e.label) || (t.url && e.url);
        });
    }, [customLinks, customLinkErrors, customLinkTouched]);

    const onProfileImageRemove = () => {
        setImage(null);
        setImagePreview(undefined);
        if (account?.name) sessionStorage.removeItem(`${account.name}_image`);
    };

    const onProfileImageUpload = async (base64Image?: string) => {
        if (!base64Image) return;
        try {
            const stagingUrl = await uploadToCloudinary(base64Image);
            if (stagingUrl) {
                setCropSourceUrl(stagingUrl);
                setCropOpen(true);
            }
        } catch (error) {
            console.error("Image upload error:", error);
        }
    };

    // When user confirms crop, we receive a Cloudinary URL with transforms
    const handleCropSave = (finalTransformedUrl: string) => {
        setImage(finalTransformedUrl);
        setImagePreview(finalTransformedUrl);
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    // When user cancels crop (keeps current avatar untouched)
    const handleCropCancel = () => {
        setCropOpen(false);
        setCropSourceUrl(null);
    };

    const handleNextOrExit = useCallback(async () => {
        if (!uuid || !account || isSaving) return;
        setDescriptionError(undefined);

        // Validate partially filled custom links
        let touchedErrorsFound = false;
        const updatedTouched = [...customLinkTouched];
        const updatedErrors = [...customLinkErrors];

        customLinks.forEach((link, index) => {
            const hasLabel = link.label.trim() !== "";
            const hasUrl = link.url.trim() !== "";

            if ((hasLabel || hasUrl) && (!hasLabel || !hasUrl)) {
                if (!updatedTouched[index])
                    updatedTouched[index] = { label: false, url: false };
                if (!updatedErrors[index])
                    updatedErrors[index] = { label: false, url: false };
                if (!hasLabel) {
                    updatedTouched[index].label = true;
                    updatedErrors[index].label = true;
                }
                if (!hasUrl) {
                    updatedTouched[index].url = true;
                    updatedErrors[index].url = true;
                }
                touchedErrorsFound = true;
            }
        });

        if (touchedErrorsFound) {
            setCustomLinkTouched(updatedTouched);
            setCustomLinkErrors(updatedErrors);
            return;
        }

        setIsSaving(true);
        try {
            const nonEmptyCustomLinks = customLinks.filter(
                (l) => l.label.trim() !== "" || l.url.trim() !== ""
            );
            await Account.update(
                uuid,
                fullName,
                description,
                address,
                phone,
                email,
                instagram,
                facebook,
                twitter,
                getPreferredLanguage(),
                image,
                nonEmptyCustomLinks
            );

            navigate("/");
        } catch (err) {
            setDescriptionError(
                (err as CustomError).response?.data?.error ||
                    "An error occurred while updating the profile."
            );
            setIsSaving(false);
        }
    }, [
        uuid,
        account,
        fullName,
        description,
        address,
        phone,
        email,
        instagram,
        facebook,
        twitter,
        image,
        customLinks,
        customLinkErrors,
        customLinkTouched,
        navigate,
        isSaving,
    ]);

    return (
        <>
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Avatar Hero ── */}
                <Stack
                    ref={heroRef}
                    sx={{
                        width: "100%",
                        minHeight: 220,
                        position: "relative",
                        zIndex: 0,
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        transition: "background 0.3s ease",
                        pt: "48px",
                        pb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Back arrow */}
                    <ButtonBase
                        onClick={handleNextOrExit}
                        disabled={isSaving}
                        sx={{
                            position: "absolute",
                            top: 12,
                            left: 10,
                            zIndex: 2,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isSaving ? (
                            <CircularProgress size={18} sx={{ color: "white" }} />
                        ) : (
                            <ColoredIcon icon={BackIcon} color="white" size="28px" />
                        )}
                    </ButtonBase>

                    {/* Avatar + hint */}
                    <Stack
                        flex={1}
                        alignItems="center"
                        justifyContent="center"
                        spacing={1.5}
                    >
                        {/* Tappable avatar */}
                        <ButtonBase
                            onClick={() => {
                                if (imagePreview) {
                                    setCropSourceUrl(
                                        originalForCrop ?? imagePreview
                                    );
                                    setCropOpen(true);
                                } else {
                                    openAvatarPicker();
                                }
                            }}
                            sx={{ borderRadius: "50%", position: "relative" }}
                        >
                            <Box
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: "50%",
                                    border: "3px solid rgba(255,255,255,0.5)",
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    ...(imagePreview
                                        ? {
                                              backgroundImage: `url(${imagePreview})`,
                                              backgroundSize: "cover",
                                              backgroundPosition: "center",
                                          }
                                        : {
                                              bgcolor: primary,
                                          }),
                                }}
                            >
                                {!imagePreview && (
                                    <PersonOutlineIcon
                                        sx={{ fontSize: 48, color: "white" }}
                                    />
                                )}
                            </Box>
                            {/* Camera badge */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 4,
                                    right: 4,
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    bgcolor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                }}
                            >
                                <CameraAltRounded
                                    sx={{
                                        fontSize: 16,
                                        color: "text.secondary",
                                    }}
                                />
                            </Box>
                        </ButtonBase>

                        {/* Frosted hint pill */}
                        <Box
                            sx={{
                                bgcolor: "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                borderRadius: "16px",
                                px: 1.5,
                                py: 0.5,
                            }}
                        >
                            <Typography
                                fontSize="12px"
                                fontWeight={500}
                                color="white"
                            >
                                {imagePreview
                                    ? "Tap to change photo"
                                    : "Add your photo"}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition:
                                "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                        }}
                    >
                        <ButtonBase
                            onClick={handleNextOrExit}
                            disabled={isSaving}
                            sx={{ p: 0.5 }}
                        >
                            {isSaving ? (
                                <CircularProgress size={18} sx={{ color: primary }} />
                            ) : (
                                <ColoredIcon
                                    icon={BackIcon}
                                    color={primary}
                                    size="28px"
                                />
                            )}
                        </ButtonBase>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                            ml={0.5}
                            noWrap
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {fullName || "Edit Profile"}
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Bottom Sheet Content ── */}
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        boxSizing: "border-box",
                        px: 2,
                        pt: 0.5,
                        pb: "calc(4vh + 90px)",
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                    spacing={2}
                >
                    {/* Display Name */}
                    <TextField
                        value={fullName}
                        onChange={(e) => setFullName(e.currentTarget.value)}
                        placeholder="Your name or brand"
                        variant="underlined"
                        nextRef={addressInputRef}
                        fullWidth
                        sx={{
                            "& input": {
                                fontFamily: FONTS.display,
                                fontSize: "20px",
                                fontWeight: 600,
                            },
                        }}
                    />

                    {/* ABOUT */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        About
                    </Typography>
                    <Stack sx={CARD_STYLES}>
                        <AIDescriptionField
                            placeholder="Enter your profile description"
                            value={description}
                            setText={setDescription}
                            contextType="profile_description"
                            uuid={uuid}
                            brandKey={brand_id}
                            width="100%"
                        />
                        {descriptionError && (
                            <Typography
                                color="error.main"
                                fontSize="13px"
                                mt={1}
                            >
                                {descriptionError}
                            </Typography>
                        )}
                    </Stack>

                    {/* CONTACT */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Contact
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2.5}>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <LocationOnRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Google Maps Location
                                </Typography>
                            </Stack>
                            <TextField
                                value={address}
                                onChange={(e) =>
                                    setAddress(e.currentTarget.value)
                                }
                                placeholder="https://maps.app.goo.gl/..."
                                variant="underlined"
                                ref={addressInputRef}
                                nextRef={phoneInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <LocalPhoneRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Phone
                                </Typography>
                            </Stack>
                            <TextField
                                value={phone}
                                onChange={(e) =>
                                    setPhone(e.currentTarget.value)
                                }
                                placeholder="+34123456789"
                                variant="underlined"
                                ref={phoneInputRef}
                                nextRef={emailInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <EmailRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Contact Email
                                </Typography>
                            </Stack>
                            <TextField
                                value={email}
                                onChange={(e) =>
                                    setEmail(e.currentTarget.value)
                                }
                                placeholder="email@gmail.com"
                                variant="underlined"
                                ref={emailInputRef}
                                nextRef={instagramInputRef}
                            />
                        </Stack>
                    </Stack>

                    {/* SOCIAL MEDIA */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Social Media
                    </Typography>
                    <Stack sx={CARD_STYLES} spacing={2.5}>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <Instagram
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Instagram
                                </Typography>
                            </Stack>
                            <TextField
                                value={instagram}
                                onChange={(e) =>
                                    setInstagram(e.currentTarget.value)
                                }
                                placeholder="URL or Account Name"
                                variant="underlined"
                                ref={instagramInputRef}
                                nextRef={twitterInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <X
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    X (Twitter)
                                </Typography>
                            </Stack>
                            <TextField
                                value={twitter}
                                onChange={(e) =>
                                    setTwitter(e.currentTarget.value)
                                }
                                placeholder="URL or Account Name"
                                variant="underlined"
                                ref={twitterInputRef}
                                nextRef={facebookInputRef}
                            />
                        </Stack>
                        <Stack spacing={0.75}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={iconBoxSx}>
                                    <Facebook
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Facebook
                                </Typography>
                            </Stack>
                            <TextField
                                value={facebook}
                                onChange={(e) =>
                                    setFacebook(e.currentTarget.value)
                                }
                                placeholder="URL or Account Name"
                                variant="underlined"
                                ref={facebookInputRef}
                                nextRef={
                                    customLinkRefs.current[0]?.labelRef
                                }
                            />
                        </Stack>
                    </Stack>

                    {/* CUSTOM LINKS */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Custom Links
                    </Typography>

                    {customLinks.map((link, index) => {
                        if (!customLinkRefs.current[index]) {
                            customLinkRefs.current[index] = {
                                labelRef: React.createRef(),
                                urlRef: React.createRef(),
                            };
                        }
                        return (
                            <Stack
                                key={index}
                                sx={CARD_STYLES}
                                spacing={1.5}
                            >
                                {/* Header row */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Box sx={iconBoxSx}>
                                            <LinkRounded
                                                sx={{
                                                    fontSize: 18,
                                                    color: primary,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            fontFamily={FONTS.display}
                                            fontSize="14px"
                                            fontWeight={600}
                                            color="text.primary"
                                        >
                                            {link.label ||
                                                `Link ${index + 1}`}
                                        </Typography>
                                    </Stack>
                                    <ButtonBase
                                        onClick={() =>
                                            removeCustomLink(index)
                                        }
                                        sx={{
                                            color: "text.secondary",
                                            borderRadius: "50%",
                                            p: 0.5,
                                        }}
                                    >
                                        <CloseRounded
                                            sx={{ fontSize: 18 }}
                                        />
                                    </ButtonBase>
                                </Stack>

                                {/* Label field */}
                                <Stack spacing={0.5}>
                                    <Typography sx={FORM_LABEL_SX}>
                                        Label
                                    </Typography>
                                    <TextField
                                        value={link.label}
                                        placeholder="e.g. YouTube"
                                        onChange={(e) =>
                                            updateCustomLink(
                                                index,
                                                "label",
                                                e.currentTarget.value
                                            )
                                        }
                                        onBlur={() =>
                                            touchCustomLink(index, "label")
                                        }
                                        error={
                                            customLinkTouched[index]?.label &&
                                            customLinkErrors[index]?.label
                                        }
                                        helperText={
                                            customLinkErrors[index]?.label
                                                ? "Label is required"
                                                : ""
                                        }
                                        ref={
                                            customLinkRefs.current[index]
                                                .labelRef
                                        }
                                        nextRef={
                                            customLinkRefs.current[index]
                                                .urlRef
                                        }
                                        variant="underlined"
                                    />
                                </Stack>

                                {/* URL field */}
                                <Stack spacing={0.5}>
                                    <Typography sx={FORM_LABEL_SX}>
                                        URL
                                    </Typography>
                                    <TextField
                                        value={link.url}
                                        placeholder="https://example.com"
                                        onChange={(e) =>
                                            updateCustomLink(
                                                index,
                                                "url",
                                                e.currentTarget.value
                                            )
                                        }
                                        onBlur={() =>
                                            touchCustomLink(index, "url")
                                        }
                                        error={
                                            !!(
                                                customLinkTouched[index]
                                                    ?.url &&
                                                customLinkErrors[index]?.url
                                            )
                                        }
                                        helperText={
                                            customLinkTouched[index]?.url &&
                                            customLinkErrors[index]?.url
                                                ? "Valid URL required"
                                                : ""
                                        }
                                        ref={
                                            customLinkRefs.current[index]
                                                .urlRef
                                        }
                                        nextRef={
                                            customLinkRefs.current[index + 1]
                                                ?.labelRef
                                        }
                                        variant="underlined"
                                    />
                                </Stack>
                            </Stack>
                        );
                    })}

                    {/* Add Link button */}
                    <ButtonBase
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderRadius: "12px",
                            py: 1,
                            px: 1.5,
                        }}
                        onClick={() => {
                            setCustomLinks([
                                ...customLinks,
                                { label: "", url: "" },
                            ]);
                            setCustomLinkErrors([
                                ...customLinkErrors,
                                { label: false, url: false },
                            ]);
                            setCustomLinkTouched([
                                ...customLinkTouched,
                                { label: false, url: false },
                            ]);
                        }}
                    >
                        <Box sx={iconBoxSx}>
                            <AddRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography
                            fontWeight={600}
                            fontSize="14px"
                            color={primary}
                        >
                            Add Custom Link
                        </Typography>
                    </ButtonBase>
                </Stack>
            </Stack>

            <BottomButton
                sticky
                onClick={handleNextOrExit}
                disabled={hasErrors || isSaving}
            >
                Save Changes
            </BottomButton>

            {/* Avatar crop modal */}
            <ImageCropperModal
                open={cropOpen}
                src={cropSourceUrl}
                onCancel={handleCropCancel}
                onSave={handleCropSave}
                onDelete={onProfileImageRemove}
                mode="avatar"
                outSize={{ w: 256, h: 256 }}
            />
        </>
    );
};

export default ProfileEdit;
